import { BufferGeometry, Mesh, Object3D, SRGBColorSpace, Vector2, WebGLRenderer } from "three";
import { acceleratedRaycast, computeBoundsTree, disposeBoundsTree } from "three-mesh-bvh";
import { assert } from "../utils";
import { PreparingObject } from "../objects/preparing_object";
// Use BVH algorithm for faster raycasting
BufferGeometry.prototype.computeBoundsTree = computeBoundsTree;
BufferGeometry.prototype.disposeBoundsTree = disposeBoundsTree;
Mesh.prototype.raycast = acceleratedRaycast;
export class ThreeRenderer {
    constructor(width, height) {
        this.frameData = {
            frameIndex: 0,
            options: {
                useLocalCache: true,
                globeNetworkRequests: {
                    requestSorting: true,
                    maxInFlight: 128,
                },
                tilesetNetworkRequests: {
                    requestSorting: true,
                    maxInFlight: 32,
                },
                debug: {
                    sunLightDirection: false,
                    tileBoundingVolumes: false,
                    tileBoundingVolumesShowRegion: true,
                    tileBoundingVolumesShowBox: true,
                    tileBoundingVolumesShowRegionSphere: false,
                    tileBoundingVolumesShowBoxSphere: false,
                    tileFrustumCulling: true,
                    globeTileFrustumCulling: true,
                    imageryBackfaceCulling: true,
                    imageryTileBoundingVolumes: false,
                    imageryLayerBoundingVolumes: false,
                    geometryTileBoundingVolumes: false,
                },
            },
        };
        this.resizeListeners = [];
        // Force Z-up
        Object3D.DEFAULT_UP.set(0, 0, 1);
        this.renderer = new WebGLRenderer({
            logarithmicDepthBuffer: true,
            preserveDrawingBuffer: true,
        });
        // @ts-ignore
        this.renderer.outputColorSpace = SRGBColorSpace;
        this.renderer.debug.checkShaderErrors = false;
        this.renderer.autoClear = false;
        this.renderer.localClippingEnabled = true;
        assert(this.renderer.capabilities.isWebGL2, "We require WebGL2 support");
        this.renderer.setSize(width, height);
        // @ts-ignore
        this.gl = this.renderer.getContext();
    }
    get config() {
        return this.frameData.options;
    }
    /**
     * Internal function for incrementing the frame index
     */
    nextFrame() {
        this.frameData.frameIndex += 1;
    }
    /**
     * Add a resize listener
     * @param listener
     */
    addResizeListener(listener) {
        this.resizeListeners.push(listener);
    }
    /**
     * Removes the given listener for the set of listeners, if it exists in the set. Otherwise does nothing.
     * @param listener
     */
    removeResizeListener(listener) {
        const idx = this.resizeListeners.findIndex((v) => v === listener);
        if (idx >= 0) {
            this.resizeListeners.splice(idx, 1);
        }
    }
    /**
     * Returns the DOM element being used as a rendering target by the renderer
     *
     * Will never change between calls
     */
    getDomElement() {
        return this.renderer.domElement;
    }
    /**
     * Resize the drawing buffers to match the requested size.
     *
     * Use this when the canvas changes size to update the drawing buffers
     *
     * @param width Width in pixels
     * @param height Height in pixels
     */
    setSize(width, height) {
        for (const fn of this.resizeListeners) {
            fn(this, this.renderer, width, height);
        }
        this.renderer.setSize(width, height);
    }
    /**
     * Returns the current size of the drawing surfaces
     *
     * @param target Vector2 instance to write the results into
     */
    getSize(target = new Vector2()) {
        return this.renderer.getSize(target);
    }
    /**
     * Performs a full rendering cycle with the given scene and camera.
     *
     * This will call 'prepare' on each 'PreparingObject' in all the provided scenes, then will execute the list of
     * passes in the provided order.
     *
     * @param scenes The scenes to render
     * @param passes The render passes to use
     * @param camera The camera to render the scene from
     */
    render(scenes, passes, camera) {
        for (const scene of scenes) {
            const objStack = [scene];
            while (objStack.length > 0) {
                const obj = objStack.pop();
                if (obj instanceof PreparingObject) {
                    obj.prepare(this, camera, this.frameData);
                    // objStack.push(...obj.children);
                }
                else {
                    objStack.push(...obj.children);
                }
            }
        }
        camera.updateMatrixWorld();
        for (const pass of passes) {
            pass.render(this, this.renderer, camera.getCurrentCamera(), scenes);
        }
    }
}
